import { useMemo } from "react";
import useSWR from "swr";
import { Address } from "viem";

import { getServerUrl } from "config/backend";
import { invertUsdPriceNumber, shouldInvertChartPriceNumber } from "domain/prices/chartInversion";
import { getNormalizedTokenSymbol, getToken } from "sdk/configs/tokens";

export type PriceDelta = {
  close: number;
  deltaPercentage: number;
  deltaPercentageStr: string;
  deltaPrice: number;
  high: number;
  low: number;
  open: number;
  tokenSymbol: string;
};

export type PriceDeltaMap = Partial<Record<Address, PriceDelta>>;

export function use24hPriceDeltaMap(
  chainId: number,
  tokenAddresses: (Address | undefined)[]
): PriceDeltaMap | undefined {
  const { data } = useSWR<
    {
      close: number;
      high: number;
      low: number;
      open: number;
      tokenSymbol: string;
    }[]
  >([chainId, "use24PriceDelta"], {
    fetcher: () => fetch(getServerUrl(chainId, "oracle-keeper/prices/24h")).then((res) => res.json()),
  });

  const priceDeltas = useMemo(() => {
    return Object.fromEntries(
      tokenAddresses
        .filter((tokenAddress): tokenAddress is Address => Boolean(tokenAddress))
        .map((tokenAddress) => {
          const token = getToken(chainId, tokenAddress);

          const tokenDelta = data?.find(
            (candle) =>
              candle.tokenSymbol === token.symbol || candle.tokenSymbol === getNormalizedTokenSymbol(token.symbol)
          );

          if (!tokenDelta) {
            return [tokenAddress, undefined];
          }

          let open = tokenDelta.open;
          let close = tokenDelta.close;
          let high = tokenDelta.high;
          let low = tokenDelta.low;

          if (shouldInvertChartPriceNumber(close) && open > 0 && close > 0 && high > 0 && low > 0) {
            const invOpen = invertUsdPriceNumber(open);
            const invClose = invertUsdPriceNumber(close);
            const invHigh = invertUsdPriceNumber(low);
            const invLow = invertUsdPriceNumber(high);
            open = invOpen;
            close = invClose;
            high = invHigh;
            low = invLow;
          }

          const deltaPrice = open !== 0 ? close - open : 0;
          const deltaPercentage = open !== 0 ? (deltaPrice * 100) / open : 0;
          const deltaPercentageStr =
            deltaPercentage > 0 ? `+${deltaPercentage.toFixed(2)}%` : `${deltaPercentage.toFixed(2)}%`;

          return [
            tokenAddress,
            { ...tokenDelta, open, close, high, low, deltaPrice, deltaPercentage, deltaPercentageStr },
          ];
        })
    );
  }, [chainId, data, tokenAddresses]);

  return priceDeltas;
}
